import type { ApiClient, Schemas } from '@argzombie/api-client';
import { ok } from './api';

/*
 * The Effects admin calls (card 9's AdminEffectEndpoints), all through the typed client.
 *
 * | Function | Endpoint |
 * |---|---|
 * | `playerEffects` | `GET /admin/players/{id}/effects` (instances, presence, zones, the active view) |
 * | `explainEffect` | `GET /admin/players/{id}/effects/explain?memberId&key` (the evaluator's trace) |
 * | `applyEffect` | `POST /admin/players/{id}/effects/apply` (Admin, audited) |
 * | `endEffect` | `POST /admin/effects/{instanceId}/end` (Admin, audited) |
 */

export type PlayerEffects = Schemas['AdminEffectsResponse'];
export type EffectInstanceRow = Schemas['EffectInstanceView'];
export type EffectExplain = Schemas['EffectExplain'];
export type EffectExplainStep = Schemas['EffectExplainStep'];

export const playerEffects = async (client: ApiClient, playerId: string) =>
  ok(await client.GET('/admin/players/{id}/effects', { params: { path: { id: playerId } } }));

export const explainEffect = async (
  client: ApiClient,
  playerId: string,
  memberId: string,
  key: string,
) =>
  ok(
    await client.GET('/admin/players/{id}/effects/explain', {
      params: { path: { id: playerId }, query: { memberId, key } },
    }),
  );

export const applyEffect = async (
  client: ApiClient,
  playerId: string,
  body: { effectId: string; target: string | null; reason: string },
) =>
  ok(
    await client.POST('/admin/players/{id}/effects/apply', {
      params: { path: { id: playerId } },
      body,
    }),
  );

export const endEffect = async (client: ApiClient, instanceId: number, reason: string) =>
  ok(
    await client.POST('/admin/effects/{instanceId}/end', {
      params: { path: { instanceId } },
      body: { reason },
    }),
  );

/** One explain step as a line: "effect grief add -2 (x1, resisted 1)", "clamp statCap -2 → -2", ... */
export function stepText(s: EffectExplainStep): string {
  const parts: string[] = [s.kind ?? ''];
  if (s.effectId) parts.push(s.effectId);
  if (s.source) parts.push(`[${s.source}]`);
  if (s.op) parts.push(s.op);
  if (s.value != null) parts.push(String(s.value));
  const extra: string[] = [];
  if (s.stacks != null && s.stacks > 1) extra.push(`x${s.stacks}`);
  if (s.resisted) extra.push(`resisted ${s.resisted}`);
  if (s.untilMs != null) extra.push(`until ${new Date(s.untilMs).toLocaleString()}`);
  if (extra.length) parts.push(`(${extra.join(', ')})`);
  if (s.rule) parts.push(s.rule);
  if (s.from != null || s.to != null) parts.push(`${s.from ?? ''} → ${s.to ?? ''}`);
  return parts.filter((p) => p !== '').join(' ');
}
